import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as slices from "../../../employee/slices/employeeSlice";
import * as services from "../../services/reportApi";
import { generateRandomString } from "utils/helper";
import { setIsLoading } from "../../slices/reportSlice";
import dayjs from "dayjs"

function usePND1Report() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [search, setSearch] = useState({});
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [showReport, setShowReport] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [year, setYear] = useState(dayjs().year());
    const [pdfUrl, setPdfUrl] = useState(null);
    const [searchValues, setSearchValues] = useState({});
    const [isDownloadDoc, setIsDownloadDoc] = useState(false);
    const [isDownload, setIsDownload] = useState(false);
    const [isDownloadTxt, setIsDownloadTxt] = useState(false);
    const [isDownloadZip, setIsDownloadZip] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const previewPND1ReportPdf = useCallback(async (params = {}, changeYear) => {
        try {
            dispatch(setIsLoading(true));

            const response = await services.ExportPND1ReportPdf({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...params,
                ...params.pagination,
                ...search,
                ...pagination,
                isReport: "Y",
                sortBy: params.sortBy,
                year: params.year,
                month: params?.month?.format?.("MM") || params?.month
            })

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            setPdfUrl(url);

        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [])

    const exportPND1ReportDocument = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadDoc(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportPND1ReportDocumentPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Cover-Page-PND1.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadDoc(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND1Report = useCallback(
        async (params = {}, changeYear) => {
            setIsDownload(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportPND1ReportPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Attachment-PND1.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownload(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND1ReportTxt = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadTxt(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportPND1ReportTxt({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "PND1.txt");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadTxt(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND1ReportZip = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadZip(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportPND1ReportZip({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "PND1.zip");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadZip(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const handleSearch = (values) => {
        const selectedYear = values.year?.year() || dayjs().year();
        setShowReport(true);
        setSearchValues(values);
        setSearch({ year: selectedYear, month: values.month });
        previewPND1ReportPdf({
            year: selectedYear,
            month: values.month
        });
    };


    return {
        isModalOpen,
        showReport,
        pdfUrl,
        setYear,
        handleSearch,
        setIsModalOpen,
        showModal,
        exportPND1ReportDocument,
        exportPND1Report,
        exportPND1ReportTxt,
        exportPND1ReportZip,
        searchValues,
        isDownloadDoc,
        isDownload,
        isDownloadTxt,
        isDownloadZip
    }
}

export default usePND1Report;